export interface CustomError {
    cause?: number;
    message?: string;
    error?: string;
}

/**
 * This determines whether an API call should be retried or not based on the failure count and the error received.
 * It checks if the cause of the error is not 400 or 429.
 * HTTP status codes 400 and 429 represent a bad request and too many requests respectively.
 * If the error cause is either of these, it means that retrying the request won't likely change the outcome,
 * so it returns false.
 * It also checks if the failureCount is less than 3. This is to prevent an infinite loop of retries.
 * If the API call has already failed 3 times, it's likely that it will keep failing, so it returns false.
 * If neither of the above conditions are met, it returns true, indicating that the API call should be retried.
 * @param [failureCount] The number of times the API call has failed.
 * @param [error] The error received from the API call.
 */
export function retryLogic(failureCount: number, error: CustomError): boolean {
    return ![400, 401, 403, 404, 405, 413, 429].includes(error.cause!) && failureCount < 3;
}

/**
 * Converts an async iterator to a readable stream.
 * https://developer.mozilla.org/docs/Web/API/ReadableStream#convert_async_iterator_to_stream
 *
 * @param [iterator] Async iterator
 * @returns Readable stream
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function iteratorToStream(iterator: any): ReadableStream<string> {
    return new ReadableStream({
        async pull(controller) {
            const { value, done } = await iterator.next();

            if (done) {
                controller.close();
            } else {
                controller.enqueue(value);
            }
        },
    });
}

export const genericNetworkFailureText: string = "Network response was not ok";
